import React, { useCallback, useContext, useEffect, useState } from "react";
import { useTable, usePagination } from "react-table";
import cookies from "../../../../../../CommonFunction/cookies";
import DataTable from "../../../Elements/DataTable";
import Pagination from "../../../Elements/Pagination";
import EmptyDataAlert from "../../../Elements/EmptyDataAlert";
import UserProfile from "../UserProfile";
import LoanDetail from "../../LoanSearch/LoanDetail";
import { fetchUserList } from "../../../../../../API/Bank/CustomerSearch";
import Loader from "../../../../../../CommonFunction/Loader";
import { UserContext } from "../../../../../../App";

const BankCustomerSearch = () => {
  const [loading, setLoading] = useState(true);
  const jwtToken = cookies.get("jwt");
  const [userData, setUserData] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedUcid, setSelectedUcid] = useState("");
  const [loanId, setLoanId] = useState("");
  const [showDetails, setShowDetails] = useState(false);
  const [paginationData, setPaginationData] = useState({
    totalDocs: 0,
    totalPages: 1,
    page: 1,
    limit: 10,
    hasNextPage: false,
    hasPrevPage: false,
    nextPage: null,
    prevPage: null,
  });
  const decodedData = useContext(UserContext);
  const bankID = decodedData?.bank;
  const branchID = decodedData?.branch;
  const role = decodedData?.role;

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = useCallback(
    async (page = 1, query = searchQuery) => {
      setLoading(true);
      try {
        const data = await fetchUserList(
          jwtToken,
          page,
          query,
          bankID,
          branchID,
          role
        );
        if (data?.data) {
          setUserData(data?.data);
          setPaginationData({
            totalDocs: data.totalDocs,
            totalPages: data.totalPages,
            page: data.page,
            limit: data.limit,
            hasNextPage: data.hasNextPage,
            hasPrevPage: data.hasPrevPage,
            nextPage: data.nextPage,
            prevPage: data.prevPage,
          });
        } else {
          setUserData([]);
        }
        setLoading(false);
      } catch (error) {
        setSearchQuery("");
        setLoading(false);
      }
    },
    [jwtToken]
  );

  const handleLoanDetails = (loanId) => {
    setLoanId(loanId);
    setShowDetails(true);
  };

  const handleButtonClick = (Ucid) => {
    setSelectedUcid(Ucid);
    setShowDetails(true);
  };

  const handleCloseDetail = () => {
    setLoanId("");
    setSelectedUcid("");
    setShowDetails(false);
  };

  const handleSearch = async () => {
    setLoading(true);
    fetchData(1, searchQuery);
  };

  const columns = React.useMemo(
    () => [
      {
        Header: "Borrower Name",
        accessor: "name",
        Cell: ({ row }) => (
          <div className="text-center border-bgclr2">
            <div className="border-bgclr2 mx-6">
              {row?.original?.first_name || ""}{" "}
              {row?.original?.middle_name || ""}{" "}
              {row?.original?.last_name || ""} {row?.original?.name || ""}
            </div>
          </div>
        ),
      },
      {
        Header: "Borrower Type",
        accessor: "role",
        Cell: ({ value }) => (
          <div className="text-center border-bgclr2">
            <div className="border-bgclr2 p-2 mx-6">
              {value === "user" ? "Individual" : "Organization"}
            </div>
          </div>
        ),
      },
      {
        Header: "UCI",
        accessor: "system_id",
        Cell: ({ value }) => (
          <div className="text-center border-bgclr2">
            <button
              onClick={() => handleButtonClick(value)}
              className="border-bgclr2 p-2 mx-6"
            >
              {value ? value : "Null"}
            </button>
          </div>
        ),
      },
      {
        Header: "Aadhaar / GST",
        accessor: "personal",
        Cell: ({ row }) => (
          <div className="text-center border-bgclr2">
            <div className="border-bgclr2 p-2 mx-6">
              {row?.original?.personal?.adhaar_card?.number || ""}{" "}
              {row?.original?.personal?.gst_number || ""}
            </div>
          </div>
        ),
      },
      {
        Header: "PAN Number",
        accessor: "personal.pancard",
        Cell: ({ value }) => (
          <div className="text-center border-bgclr2">
            <div className="border-bgclr2 p-2 mx-6">
              {value ? value : "Null"}
            </div>
          </div>
        ),
      },
      {
        Header: "Linked Loan",
        accessor: "loans",
        Cell: ({ row }) => (
          <div>
            {row?.original?.loans?.map((loan) => (
              <div className="my-1 px-5">
                <button onClick={() => handleLoanDetails(loan._id)}>
                  <p>{loan.identity_number}</p>
                </button>
              </div>
            ))}
          </div>
        ),
      },
    ],
    []
  );

  const { getTableProps, getTableBodyProps, headerGroups, page, prepareRow } =
    useTable(
      {
        columns,
        data: userData,
        initialState: { pageIndex: 0 },
        manualPagination: true,
      },
      usePagination
    );

  const nextPage = useCallback(() => {
    if (paginationData.hasNextPage)
      fetchData(paginationData.page + 1, searchQuery);
  }, [fetchData, searchQuery, paginationData]);

  const previousPage = useCallback(() => {
    if (paginationData.hasPrevPage)
      fetchData(paginationData.page - 1, searchQuery);
  }, [fetchData, searchQuery, paginationData]);

  return (
    <>
      <div className="root-style">
        {!showDetails ? (
          <div>
            <div className="searchBar-style">
              <input
                id="searchInput"
                type="search"
                placeholder="Search by Name, Loan Number, Aadhaar Number and PAN Number...."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="searchBar-input"
              />
              <button onClick={() => handleSearch()} className="searchBar-btn">
                Search
              </button>
            </div>
            {loading ? (
              <div className="flex justify-center h-screen">
                <Loader />
              </div>
            ) : (
              <>
                {userData.length === 0 ? (
                  <EmptyDataAlert title="No Data Available" />
                ) : (
                  <div className="mt-6">
                    <div className="pt-3 overflow-y-auto">
                      <DataTable
                        getTableProps={getTableProps}
                        headerGroups={headerGroups}
                        getTableBodyProps={getTableBodyProps}
                        page={page}
                        prepareRow={prepareRow}
                      />
                    </div>
                    <Pagination
                      previousPage={previousPage}
                      nextPage={nextPage}
                      canPreviousPage={paginationData.hasPrevPage}
                      canNextPage={paginationData.hasNextPage}
                      paginationData={{
                        page: paginationData.page,
                        totalPages: paginationData.totalPages,
                      }}
                    />
                  </div>
                )}
              </>
            )}
          </div>
        ) : (
          <div>
            {selectedUcid && (
              <div>
                <UserProfile onClose={handleCloseDetail} ucid={selectedUcid} />
              </div>
            )}
            {loanId && (
              <div>
                <LoanDetail onClose={handleCloseDetail} loanId={loanId} />
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
};

export default BankCustomerSearch;
